import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    ArrowRight,
    Loader2,
    Sparkles,
    AlertTriangle,
    Stethoscope,
    Lock,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { QUIZ_QUESTIONS } from "@/constants/dosha";
import { describeSymptom } from "@/constants/symptoms";
import { TID } from "@/constants/testIds";

const PENDING_SUB_KEY = "sushen_pending_submission";

function isRelevant(q, answersByQid, dyesnoByQid) {
    if (!q.parent_quiz_id) return true;
    const parent = answersByQid[q.parent_quiz_id];
    if (!parent) return false;
    // Compare on answer_text (case-insensitive contains) — the trigger_answer is defined in words
    return (parent.answer_text || "").toLowerCase() === (q.trigger_answer || "").toLowerCase();
}

export default function UnifiedQuiz() {
    const nav = useNavigate();
    const { user, requireAuth, refresh } = useAuth();
    const [phase, setPhase] = useState("dosha"); // dosha | symptom | redFlag | results | loading
    const [doshaAnswers, setDoshaAnswers] = useState({}); // {questionId: 'vata'|...}
    const [doshaIdx, setDoshaIdx] = useState(0);

    const [symptomQuestions, setSymptomQuestions] = useState([]);
    const [symptomAnswers, setSymptomAnswers] = useState({}); // {quiz_id: {answer_id, answer_text}}
    const [symptomIdx, setSymptomIdx] = useState(0);
    const [saving, setSaving] = useState(false);
    const [submission, setSubmission] = useState(null); // {submission_id, submission_token, teaser, computed, red_flag}
    const [fullResult, setFullResult] = useState(null);

    // Fetch symptom questions once
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/quiz/questions");
                setSymptomQuestions(data || []);
            } catch {
                // non-fatal — dosha section still works
            }
        })();
    }, []);

    // If user was anon → signed up later, auto-claim pending submission
    useEffect(() => {
        if (!user) return;
        const raw = localStorage.getItem(PENDING_SUB_KEY);
        if (!raw) return;
        (async () => {
            try {
                const { submission_token, submission_id } = JSON.parse(raw);
                if (!submission_token) return;
                await api.post("/quiz/claim", { submission_token });
                localStorage.removeItem(PENDING_SUB_KEY);
                // Refresh user (dosha profile promoted server-side)
                await refresh();
                // Fetch full result now that we own it
                const { data } = await api.get(`/quiz/submission/${submission_id}`);
                setFullResult(data);
                setSubmission({ ...data, teaser: null });
                setPhase("results");
            } catch {
                // silent — user can still take the quiz
            }
        })();
    }, [user, refresh]);

    // Ordered list of relevant symptom questions (branching-aware)
    const relevantSymptomQs = useMemo(() => {
        return (symptomQuestions || []).filter((q) => isRelevant(q, symptomAnswers, {}));
    }, [symptomQuestions, symptomAnswers]);

    const totalDosha = QUIZ_QUESTIONS.length;
    const doshaDone = QUIZ_QUESTIONS.filter((x) => doshaAnswers[x.id]).length;
    const symptomDone = relevantSymptomQs.filter((q) => symptomAnswers[q.quiz_id]).length;
    const totalRelevant = relevantSymptomQs.length;
    const overallPct = Math.round(
        ((doshaDone + symptomDone) / Math.max(1, totalDosha + totalRelevant)) * 100,
    );

    // --------- Dosha phase ---------
    const doshaQ = QUIZ_QUESTIONS[doshaIdx];
    function pickDosha(dosha) {
        setDoshaAnswers((prev) => ({ ...prev, [doshaQ.id]: dosha }));
        setTimeout(() => {
            if (doshaIdx < totalDosha - 1) setDoshaIdx((i) => i + 1);
            else if (relevantSymptomQs.length > 0) setPhase("symptom");
            else finalSubmit(doshaAnswersToArray({ ...doshaAnswers, [doshaQ.id]: dosha }), {});
        }, 200);
    }

    function doshaAnswersToArray(answersObj) {
        return QUIZ_QUESTIONS.map((x) => answersObj[x.id]).filter(Boolean);
    }

    // --------- Symptom phase ---------
    const symQ = relevantSymptomQs[symptomIdx];
    function pickSymptom(q, opt) {
        const nextAnswers = {
            ...symptomAnswers,
            [q.quiz_id]: { answer_id: opt.answer_id, answer_text: opt.answer_text },
        };
        setSymptomAnswers(nextAnswers);
        setTimeout(() => {
            // Recompute relevant list from nextAnswers
            const nextRelevant = (symptomQuestions || []).filter((qq) =>
                isRelevant(qq, nextAnswers, {}),
            );
            const nextIdx = symptomIdx + 1;
            // If red flag question was just answered "Yes", jump to red flag screen
            if (q.red_flag_screen && opt.symptom_id === "SYM_RED_FLAG") {
                finalSubmit(doshaAnswersToArray(doshaAnswers), nextAnswers, true);
                return;
            }
            if (nextIdx < nextRelevant.length) {
                setSymptomIdx(nextIdx);
            } else {
                finalSubmit(doshaAnswersToArray(doshaAnswers), nextAnswers);
            }
        }, 200);
    }

    async function finalSubmit(dArr, sAnswers, forceRedFlag = false) {
        setSaving(true);
        setPhase("loading");
        try {
            const responses = Object.entries(sAnswers).map(([qid, v]) => ({
                quiz_id: qid,
                answer_id: v.answer_id,
                answer_text: v.answer_text,
            }));
            const { data } = await api.post("/quiz/submit", {
                dosha_answers: dArr,
                responses,
            });
            setSubmission(data);
            // Store anon submission token so we can claim after signup
            if (!data.is_authed && data.submission_token) {
                localStorage.setItem(
                    PENDING_SUB_KEY,
                    JSON.stringify({
                        submission_token: data.submission_token,
                        submission_id: data.submission_id,
                    }),
                );
            }
            if (data.red_flag || forceRedFlag) {
                setPhase("redFlag");
                return;
            }
            if (data.is_authed) {
                setFullResult({ computed: data.computed });
                await refresh();
                setPhase("results");
                return;
            }
            setPhase("results");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not save. Please retry.");
            setPhase(dArr.length >= totalDosha ? "symptom" : "dosha");
        } finally {
            setSaving(false);
        }
    }

    // Trigger signup, then auto-claim + show full
    function signupAndReveal() {
        requireAuth(async () => {
            const raw = localStorage.getItem(PENDING_SUB_KEY);
            if (!raw) return;
            try {
                const { submission_token, submission_id } = JSON.parse(raw);
                await api.post("/quiz/claim", { submission_token });
                localStorage.removeItem(PENDING_SUB_KEY);
                await refresh();
                const { data } = await api.get(`/quiz/submission/${submission_id}`);
                setFullResult(data);
            } catch {
                toast.error("Could not attach your quiz. Please retry.");
            }
        });
    }

    // ============ Render ============
    if (phase === "loading") {
        return (
            <div className="veda-container py-20 text-center">
                <Loader2 className="mx-auto animate-spin text-veda-brand" size={22} />
                <p className="mt-3 text-sm text-veda-muted">
                    Reading your body's signals…
                </p>
            </div>
        );
    }

    return (
        <div
            data-testid={TID.quizPage}
            className="veda-container-narrow py-10 md:py-16"
        >
            <div className="mx-auto max-w-2xl">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                >
                    <ArrowLeft size={14} /> Home
                </Link>

                <div className="mt-6 flex items-center gap-2">
                    <span className="grid h-9 w-9 place-items-center bg-veda-brand/10 text-veda-brand">
                        <Sparkles size={16} />
                    </span>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                        {phase === "dosha"
                            ? "Part 1 of 2 · Your constitution"
                            : phase === "symptom"
                            ? "Part 2 of 2 · What's on your mind"
                            : "Your Ayurvedic report"}
                    </p>
                </div>

                {phase === "redFlag" && <RedFlagScreen onBook={() => nav("/wellness/#consult")} />}
                {phase === "results" && (
                    <ResultsView
                        submission={submission}
                        fullResult={fullResult}
                        isAuthed={!!user}
                        onSignup={signupAndReveal}
                        goDashboard={() => nav("/dashboard")}
                    />
                )}

                {(phase === "dosha" || phase === "symptom") && (
                    <>
                        <h1 className="font-display mt-2 text-3xl text-veda-onSurface md:text-5xl">
                            {phase === "dosha"
                                ? "Discover your dosha."
                                : "One more thing your Vaidya wants to know."}
                        </h1>
                        <p className="mt-3 max-w-xl text-sm text-veda-muted md:text-base">
                            {phase === "dosha"
                                ? "12 short questions to reveal your prakriti — the constitution you were born with."
                                : "A few current-life questions so we can spot symptom patterns and give you a personalised report."}
                        </p>

                        {/* Progress */}
                        <div className="mt-8">
                            <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                <span>
                                    {phase === "dosha"
                                        ? `Constitution ${doshaIdx + 1}/${totalDosha}`
                                        : `Concern ${symptomIdx + 1}/${totalRelevant}`}
                                </span>
                                <span data-testid={TID.quizProgress}>{overallPct}%</span>
                            </div>
                            <div className="mt-2 h-1 w-full bg-veda-surface2">
                                <div
                                    className="h-full bg-veda-brand transition-all duration-300"
                                    style={{ width: `${overallPct}%` }}
                                />
                            </div>
                        </div>

                        {phase === "dosha" && (
                            <DoshaQuestion
                                q={doshaQ}
                                idx={doshaIdx}
                                selected={doshaAnswers[doshaQ.id]}
                                onPick={pickDosha}
                            />
                        )}
                        {phase === "symptom" && symQ && (
                            <SymptomQuestion
                                q={symQ}
                                idx={symptomIdx}
                                selected={symptomAnswers[symQ.quiz_id]}
                                onPick={(opt) => pickSymptom(symQ, opt)}
                            />
                        )}

                        {/* Nav */}
                        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
                            <button
                                onClick={() => {
                                    if (phase === "dosha") {
                                        setDoshaIdx((i) => Math.max(0, i - 1));
                                    } else {
                                        if (symptomIdx === 0) {
                                            setPhase("dosha");
                                            setDoshaIdx(totalDosha - 1);
                                        } else {
                                            setSymptomIdx((i) => i - 1);
                                        }
                                    }
                                }}
                                disabled={phase === "dosha" && doshaIdx === 0}
                                data-testid={TID.quizBack}
                                className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-veda-onSurface disabled:opacity-50"
                            >
                                <ArrowLeft size={13} /> Back
                            </button>

                            {phase === "dosha" && doshaIdx < totalDosha - 1 && (
                                <button
                                    onClick={() => setDoshaIdx((i) => Math.min(totalDosha - 1, i + 1))}
                                    disabled={!doshaAnswers[doshaQ.id]}
                                    data-testid={TID.quizNext}
                                    className="inline-flex items-center gap-1 bg-veda-inverse px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-veda-onInverse disabled:opacity-50"
                                >
                                    Next <ArrowRight size={13} />
                                </button>
                            )}

                            {phase === "dosha" && doshaIdx === totalDosha - 1 && (
                                <button
                                    onClick={() => {
                                        if (relevantSymptomQs.length === 0)
                                            finalSubmit(doshaAnswersToArray(doshaAnswers), {});
                                        else setPhase("symptom");
                                    }}
                                    disabled={doshaDone !== totalDosha || saving}
                                    data-testid={TID.quizNext}
                                    className="inline-flex items-center gap-1 bg-veda-brand px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-white disabled:opacity-60"
                                >
                                    Continue <ArrowRight size={13} />
                                </button>
                            )}

                            {phase === "symptom" && symQ && symptomIdx < totalRelevant - 1 && (
                                <button
                                    onClick={() => setSymptomIdx((i) => Math.min(totalRelevant - 1, i + 1))}
                                    disabled={!symptomAnswers[symQ.quiz_id] && symQ.required}
                                    data-testid={TID.quizNext}
                                    className="inline-flex items-center gap-1 bg-veda-inverse px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-veda-onInverse disabled:opacity-50"
                                >
                                    Next <ArrowRight size={13} />
                                </button>
                            )}

                            {phase === "symptom" && symQ && symptomIdx === totalRelevant - 1 && (
                                <button
                                    onClick={() => finalSubmit(doshaAnswersToArray(doshaAnswers), symptomAnswers)}
                                    disabled={saving}
                                    data-testid={TID.quizSubmit}
                                    className="inline-flex items-center gap-2 bg-veda-brand px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-white disabled:opacity-60"
                                >
                                    {saving && <Loader2 size={13} className="animate-spin" />}
                                    Reveal my report
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ---- Sub-components ----

function DoshaQuestion({ q, idx, selected, onPick }) {
    return (
        <div
            key={q.id}
            data-testid={TID.quizQuestion(q.id)}
            className="mt-10 animate-fade-in"
        >
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                Constitution · Q{idx + 1}
            </p>
            <h2 className="font-display mt-2 text-2xl text-veda-onSurface md:text-3xl">
                {q.q}
            </h2>
            <div className="mt-6 flex flex-col gap-3">
                {q.options.map((opt) => {
                    const active = selected === opt.dosha;
                    return (
                        <button
                            key={opt.dosha}
                            onClick={() => onPick(opt.dosha)}
                            data-testid={TID.quizOption(q.id, opt.dosha)}
                            className={`flex items-center justify-between border p-4 text-left transition-colors md:p-5 ${
                                active
                                    ? "border-veda-brand bg-veda-brand/10"
                                    : "border-veda-border bg-veda-surface hover:border-veda-borderStrong"
                            }`}
                        >
                            <span
                                className={`text-sm md:text-base ${
                                    active
                                        ? "font-semibold text-veda-onSurface"
                                        : "text-veda-onSurface/85"
                                }`}
                            >
                                {opt.label}
                            </span>
                            <span
                                className={`ml-4 h-5 w-5 shrink-0 rounded-full border-2 ${
                                    active
                                        ? "border-veda-brand bg-veda-brand"
                                        : "border-veda-border"
                                }`}
                            />
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function SymptomQuestion({ q, idx, selected, onPick }) {
    const options = q.question_type === "yes_no"
        ? (q.answers?.length ? q.answers : [
            { answer_id: "yes", answer_text: "Yes" },
            { answer_id: "no", answer_text: "No" },
        ])
        : (q.answers || []);
    return (
        <div
            key={q.quiz_id}
            data-testid={TID.quizQuestion(q.quiz_id)}
            className="mt-10 animate-fade-in"
        >
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                {q.health_category} · Q{idx + 1}
            </p>
            <h2 className="font-display mt-2 text-2xl text-veda-onSurface md:text-3xl">
                {q.question_text}
            </h2>
            <div className="mt-6 flex flex-col gap-3">
                {options.map((opt) => {
                    const active = selected?.answer_id === opt.answer_id;
                    return (
                        <button
                            key={opt.answer_id}
                            onClick={() => onPick(opt)}
                            data-testid={TID.quizOption(q.quiz_id, opt.answer_id)}
                            className={`flex items-center justify-between border p-4 text-left transition-colors md:p-5 ${
                                active
                                    ? "border-veda-brand bg-veda-brand/10"
                                    : "border-veda-border bg-veda-surface hover:border-veda-borderStrong"
                            }`}
                        >
                            <span
                                className={`text-sm md:text-base ${
                                    active
                                        ? "font-semibold text-veda-onSurface"
                                        : "text-veda-onSurface/85"
                                }`}
                            >
                                {opt.answer_text}
                            </span>
                            <span
                                className={`ml-4 h-5 w-5 shrink-0 rounded-full border-2 ${
                                    active
                                        ? "border-veda-brand bg-veda-brand"
                                        : "border-veda-border"
                                }`}
                            />
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function RedFlagScreen({ onBook }) {
    return (
        <div
            data-testid={TID.quizRedFlag}
            className="mt-6 border border-veda-error/60 bg-veda-error/[0.06] p-6 md:p-8"
        >
            <div className="flex items-center gap-2 text-veda-error">
                <AlertTriangle size={18} />
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em]">
                    Please seek immediate care
                </p>
            </div>
            <h1 className="font-display mt-2 text-3xl text-veda-onSurface md:text-4xl">
                Some symptoms need in-person attention today.
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-veda-muted">
                Based on your answers, we recommend consulting a qualified
                medical doctor immediately rather than starting home remedies.
                Severe or rapidly worsening abdominal pain, breathing
                difficulty, fainting, or heavy bleeding are emergencies — please
                do not delay in-person care.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-veda-muted">
                Your Vaidya has been notified. Once you're stable, book a
                follow-up consultation on Sushen Herbs so we can plan
                Ayurvedic support alongside your recovery.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={onBook}
                    data-testid={TID.quizRedFlagBook}
                    className="inline-flex items-center gap-2 bg-veda-error px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white"
                >
                    <Stethoscope size={12} /> Book urgent consultation
                </button>
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 border border-veda-border bg-veda-surface px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                >
                    Back to home
                </Link>
            </div>
        </div>
    );
}

function ResultsView({ submission, fullResult, isAuthed, onSignup, goDashboard }) {
    if (!submission) return null;
    const computed = fullResult?.computed || submission?.computed;

    // AUTHED: show full result
    if (isAuthed && computed) {
        return (
            <div data-testid={TID.quizFullResult} className="mt-6">
                <h1 className="font-display text-3xl text-veda-onSurface md:text-5xl">
                    Your Ayurvedic report.
                </h1>
                <p className="mt-2 text-sm text-veda-muted">
                    Saved to your dashboard. Your Vaidya can see the same view
                    before every consult.
                </p>

                {computed.prakriti && (
                    <div className="mt-6 border border-veda-border bg-veda-surface p-5">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                            Prakriti (constitution)
                        </p>
                        <p className="font-display mt-1 text-2xl text-veda-onSurface">
                            {(computed.prakriti.primary || "").split("-").join("–")}
                        </p>
                        <div className="mt-3 flex gap-6 text-center">
                            {["vata", "pitta", "kapha"].map((d) => (
                                <div key={d}>
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted">
                                        {d}
                                    </p>
                                    <p className="font-display text-lg text-veda-onSurface">
                                        {computed.prakriti.scores?.[d] ?? 0}%
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <ReportCard label="Top concern" value={computed.top_concern || "—"} />
                    <ReportCard label="Severity" value={computed.severity || "—"} />
                    <ReportCard label="Duration" value={computed.duration || "—"} />
                    <ReportCard label="Stress level" value={computed.stress || "—"} />
                </div>

                {(computed.top_symptoms || []).length > 0 && (
                    <div className="mt-4 border border-veda-border bg-veda-surface p-5">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                            What your answers say
                        </p>
                        <ul className="mt-3 space-y-4 divide-y divide-veda-divider">
                            {computed.top_symptoms.map((s) => {
                                const info = describeSymptom(s.symptom_id);
                                if (!info) return null;
                                return (
                                    <li key={s.symptom_id} className="pt-4 first:pt-0">
                                        <div className="flex items-baseline justify-between gap-2">
                                            <p className="font-display text-base text-veda-onSurface md:text-lg">
                                                {info.name}
                                            </p>
                                            <span
                                                className={`rounded-pill px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                                                    info.color === "error"
                                                        ? "bg-veda-error text-white"
                                                        : "border border-veda-brand/40 bg-veda-brand/[0.06] text-veda-brand"
                                                }`}
                                            >
                                                intensity {s.score}
                                            </span>
                                        </div>
                                        {info.short && (
                                            <p className="mt-1 text-sm text-veda-onSurface">
                                                {info.short}
                                            </p>
                                        )}
                                        {info.dosha_view && (
                                            <p className="mt-1 text-sm italic leading-relaxed text-veda-muted">
                                                {info.dosha_view}
                                            </p>
                                        )}
                                        {info.quick_tip && (
                                            <p className="mt-2 border-l-2 border-veda-brand pl-3 text-sm text-veda-onSurface">
                                                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-brand">
                                                    Try today
                                                </span>{" "}
                                                — {info.quick_tip}
                                            </p>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                <div className="mt-6 flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={goDashboard}
                        className="inline-flex items-center gap-2 bg-veda-inverse px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onInverse"
                    >
                        Go to dashboard
                    </button>
                    <Link
                        to="/wellness/guide"
                        className="inline-flex items-center gap-2 border border-veda-brand bg-veda-brand px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white"
                    >
                        Chat with the AI Guide
                    </Link>
                </div>
            </div>
        );
    }

    // ANON: teaser + signup gate
    return (
        <div data-testid={TID.quizTeaser} className="mt-6">
            <h1 className="font-display text-3xl text-veda-onSurface md:text-5xl">
                Your snapshot is ready.
            </h1>
            {submission.teaser && (
                <p className="mt-2 text-base text-veda-onSurface md:text-lg">
                    {submission.teaser}
                </p>
            )}
            <p className="mt-2 text-sm text-veda-muted">
                Create your Sushen Herbs account to save this, unlock your full
                symptom breakdown, personalised rituals, and share the report
                with a Vaidya.
            </p>

            <div className="mt-6 border border-veda-brand/40 bg-veda-brand/[0.04] p-5 md:p-6">
                <div className="flex items-center gap-2 text-veda-brand">
                    <Lock size={14} />
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em]">
                        Full report locked
                    </p>
                </div>
                <ul className="mt-3 space-y-1.5 text-sm text-veda-onSurface">
                    <li>· Your prakriti breakdown (Vata / Pitta / Kapha %)</li>
                    <li>· All symptoms we identified, ranked by intensity</li>
                    <li>· Personalised rituals & starter Ayurvedic guidance</li>
                    <li>· One-tap consult booking with a Vaidya</li>
                </ul>
                <button
                    type="button"
                    onClick={onSignup}
                    data-testid={TID.quizSignupCta}
                    className="mt-5 inline-flex items-center gap-2 bg-veda-brand px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white"
                >
                    <Sparkles size={12} /> Sign up to see the full report
                </button>
            </div>

            <p className="mt-4 text-[10px] text-veda-muted">
                Already have an account?{" "}
                <button
                    type="button"
                    onClick={onSignup}
                    className="underline hover:text-veda-onSurface"
                >
                    Sign in and we'll attach this report to your account.
                </button>
            </p>
        </div>
    );
}

function ReportCard({ label, value }) {
    return (
        <div className="border border-veda-border bg-veda-surface p-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                {label}
            </p>
            <p className="font-display mt-1 text-base capitalize text-veda-onSurface">
                {value}
            </p>
        </div>
    );
}
